import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { LockControl } from "@/Common/Components/IconControls/LockControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { SingleSelectTextBoxControl } from "@/Common/Components/Inputs/SingleSelectTextBoxControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { SimpleDisplayModel, SimpleSearchComplete } from "@/Common/Components/Search/SimpleSearchAutocomplete";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ValueCodeDropdownItem } from "@/Patient/Components/MedicalItems/Immunization/ValueCodeDropdownItem";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemDate } from "../MedicalItemDate";
import { ImmunizationDetailEditForm, MedicalItemsImmunizationViewState } from "../MedicalItemsImmunization";
import { ConstantValues } from "./Constants/ConstantValues";
import { ValueSetType } from "./Constants/ValueSetType";
import { CodeValueSearchAutocompleteCustom, CodeValueSearchStrategy } from "./ImmunizationProfileSections/CodeValueSearchAutocompleteCustom";
import { RvqPractitionerSearchPopup } from "./ImmunizationProfileSections/RvqPractitionerSearchPopup";
import { CodeValue } from "./Models/CodeValue";
import { MedicationBatchModel } from "./Models/MedicalBatchModel";
import { PractitionerModel } from "./Models/PractitionerModel";
import { RvqMedicationPreference } from "./Models/RvqMedicationPreference";
import { RvqPractitionerPreference } from "./Models/RvqPractitionerPreference";
import { ImmunizationStateManager } from "./ImmunizationStateManager";
import { QuebecVaccinationRegistryHelper } from "./QuebecVaccinationRegistryHelper";
import { RvqAutoCompleteRow } from "./RvqAutoCompleteRow";
import { RvqLocationSelection } from "./RvqLocationSelection";

interface ImmunizationDetailsEditComponentProps {
    encounter: Encounter;
    immunization: MedicalItemImmunization;
    state: MedicalItemsImmunizationViewState;
    mutateState: (mutation: (state: MedicalItemsImmunizationViewState) => void, then?: () => void) => void;
    patientDateOfBirth: Date;

    /**
     * Whether the user is authenticated with the Quebec Vaccination Registry (if it's enabled).
     */
    isRvqAuthenticated: boolean;
}

class ImmunizationDetailsEditComponentState {
    public showPractitionerSearchPopup: boolean;
    public showExpiredLotNumbers: boolean;
    public currentForm: ImmunizationDetailEditForm;
}
class ImmunizationDetailsEditComponent extends BaseReactComponent<ImmunizationDetailsEditComponentProps, ImmunizationDetailsEditComponentState> {
    private administeredByAutocompleteInstance: SimpleSearchComplete<RvqPractitionerPreference>;
    private immunizationNameAutocompleteInstance: SimpleSearchComplete<RvqMedicationPreference>;
    private clinicianSearchStrategy: ClinicianSearchStrategy = new ClinicianSearchStrategy(cacheStore.GetActiveEmployees().map(p => Person.FromEmployee(p)));

    public constructor(props: ImmunizationDetailsEditComponentProps) {
        super(props);
        this.state = {
            showPractitionerSearchPopup: false,
            showExpiredLotNumbers: false,
            currentForm: ImmunizationDetailEditForm.CreateImmunization
        };
    }

    private get stateManager(): ImmunizationStateManager {
        return new ImmunizationStateManager(this.props);
    }

    private get lifeStagePrecisions(): LifeStageDatePrecisionEnum[] {
        return [LifeStageDatePrecisionEnum.MonthYear, LifeStageDatePrecisionEnum.Year];
    }

    private renderPractitionerSearchPopup(): JSX.Element {
        return (
            <RvqPractitionerSearchPopup
                isRvqAuthenticated={this.props.isRvqAuthenticated}
                existingPractitionerPreferences={this.props.state?.rvqPractitionerPreferences ?? []}
                onClose={() => this.mutateState(s => s.showPractitionerSearchPopup = false)}
                onPractitionerSave={(practitioner, preferences) => {
                    this.mutateState(
                        s => s.showPractitionerSearchPopup = false,
                        () => this.props.mutateState(
                            s => {
                                s.QuebecRegistryAdministeredById = practitioner.Id;
                                s.AdministeredByDisplayText = PractitionerModel.getDisplayName(practitioner);
                                s.rvqPractitionerPreferences = preferences;
                            }
                        )
                    );
                }}
            />
        );
    }

    public render(): JSX.Element {
        const helper = this.stateManager;
        const state = this.props.state;

        if (!state)
            return null;

        return (
            <div className="medical-reaction-detail-box" style={{ "minWidth": "686px" }}>
                {this.state.showPractitionerSearchPopup &&
                    this.renderPractitionerSearchPopup()
                }
                <table className="medical-problems-edit border">
                    <tbody>
                        {this.renderRow("RefusalLabel", this.renderRefusalCheckboxes(), this.isRvqEnabled)}
                        {this.renderRow(
                            "ImmunizationNameDLabel",
                            this.isRvqEnabled
                                ? this.renderImmunizationNameSimpleSearch()
                                : (
                                    <TextboxControl
                                        id="immunizationName"
                                        onMountFocus
                                        valid={!!state.ImmunizationName}
                                        onChange={val => helper.onImmunizationNameChange(val)}
                                        onAutoCompleteSelectCallBack={(code, model, value) => helper.onImmunizationAutoComplete(value)}
                                        autoCompleteItems={helper.ImmunizationNameOptions}
                                        value={state.ImmunizationName}
                                    />
                                ),
                            helper.isCreateImmunizationForm || helper.isRefuseSpecificVaccineForm || !this.isRvqEnabled,
                            this.props.state.currentForm === ImmunizationDetailEditForm.CreateImmunization && (
                                <LockControl
                                    isLocked={sessionStore.isLockedData("ImmunizationModel")}
                                    onClick={() => {
                                        sessionStore.isLockedData("ImmunizationModel")
                                            ? sessionStore.clearLockedData("ImmunizationModel")
                                            : sessionStore.setLockData("ImmunizationModel", MedicalItemsImmunizationViewState.getDataToLock(this.props.state));
                                        this.forceUpdate();
                                    }}
                                />
                            )
                        )}
                        {this.renderRow(
                            "QuebecVaccinationRegistry_ImmunizingAgentLabel",
                            <SingleSelectTextBoxControl
                                isSelected={this.isRvqEnabled && !!state.ImmunizingAgent && !!state.QuebecRegistryAgentCode}
                                displayText={QuebecVaccinationRegistryHelper.translateValueSet(state.QuebecRegistryAgentCode, state.ImmunizingAgent, ValueSetType.Agent)}
                                onRemove={() => this.props.mutateState(s => {
                                    s.ImmunizingAgent = "";
                                    s.QuebecRegistryAgentCode = "";
                                    s.SelectedAntigens = [];
                                    s.AntigensInCurrentAgent = [];
                                    s.codeValueSearchStrategy = new CodeValueSearchStrategy([]);
                                    if (!!s.ImmunizationName && s.RvqOptions.tradeNames.any(x => x.Item.DisplayText === s.ImmunizationName))
                                        MedicalItemsImmunizationViewState.clearLinkedTradeNameFields(s);
                                })}
                                onChange={value => this.onImmunizationAgentChange(value)}
                                {...helper.ImmunizationAgentProps()}
                                value={state.ImmunizingAgent}
                                disableRemoveButton={this.stateManager.IsImmunizationNameSelected}
                            />,
                            helper.isCreateImmunizationForm || helper.isRefuseSpecificVaccineForm || !this.isRvqEnabled
                        )}
                        {helper.isRefuseSpecificVaccineForm
                            && this.isRvqEnabled
                            && this.renderAntigenSelector()
                        }
                        {this.renderRow(
                            "",
                            <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                <CheckboxControl
                                    id="refusedCheckbox"
                                    checked={state.IsRefused}
                                    onChange={checked => this.props.mutateState(s => {
                                        s.IsRefused = checked;
                                    })}
                                    label={cacheStore.GetString("ImmunizationViewRefusedLabel")}
                                />
                            </div>,
                            !this.isRvqEnabled,
                            null,
                            "refusedCheckbox"
                        )}
                        {state.IsRefused
                            ? this.getVaccineRefusalElements()
                            : this.getVaccineDetailElements()
                        }
                        {this.renderRow(
                            "NotesLabel",
                            <TextAreaResizable
                                style={{ resize: "none", minHeight: "100px" }}
                                verticalResizeOnly
                                id={"exposure_Det" + (sessionStore.newId())}
                                text={state.Notes}
                                invalid={this.isRvqEnabled
                                    && sessionStore.rvq.canSubmitToRvq(this.props.immunization, false)
                                    && !isGuidNullOrUndefinedOrEmpty(state.Id)
                                    && !!state.QuebecRegistryId
                                    && !!state.SubmittedToQuebecRegistryOn
                                    && !state.Notes
                                    && state.shouldSyncWithRvq
                                }
                                isDisabled={false}
                                onChange={value => this.props.mutateState(s => s.Notes = value)}
                                setCursorStartOnFocus={false}
                                resetTextOnIdChange
                            />
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    private renderImmunizationNameSimpleSearch(): JSX.Element {
        const helper = this.stateManager;
        const state = this.props.state;

        if (!state)
            return null;

        const sortedMedicationPreferences = QuebecVaccinationRegistryHelper.getSortedPreferenceList(state.rvqMedicationPreferences);

        return (
            <SingleSelectTextBoxControl
                isSelected={helper.IsImmunizationNameSelected}
                displayText={state.ImmunizationName}
                onRemove={() => this.props.mutateState(s => MedicalItemsImmunizationViewState.clearLinkedTradeNameFields(s))}
                alternateEditComponent={(
                    <SimpleSearchComplete<RvqMedicationPreference>
                        ref={ref => this.immunizationNameAutocompleteInstance = ref}
                        componentId="immunizationName"
                        selectedItemList={[].toImmutable()}
                        onSelectionCallback={item => {
                            if (!item.Item.Data) {
                                helper.onImmunizationAutoComplete(item.Item.Id as string);
                                return;
                            }

                            const rvqPreference = this.props.state.rvqMedicationPreferences.firstOrDefault(x => x.EntityId === item.Item.Data.EntityId);
                            if (rvqPreference)
                                helper.onImmunizationAutoComplete(rvqPreference.QuebecVaccinationRegistryId, rvqPreference);
                        }}
                        value={state.ImmunizationName}
                        onTextChange={value => this.props.mutateState(s => s.ImmunizationName = value)}
                        closeDropDownAfterSelection
                        useBlurEvent={false}
                        doNotEraseListAfterSelection
                        onSearch={async searchString => {
                            let existingMedicationPreferences = sortedMedicationPreferences;
                            let tradeNames = this.props.state.RvqOptions.tradeNames.map(x => new SimpleDisplayModel({ ...x, Item: { ...x.Item }, CssClass: "" }));
                            if (!IsNullOrWhiteSpace(searchString)) {
                                existingMedicationPreferences = sortedMedicationPreferences.filter(x => x.Item.DisplayText.contains(searchString, { ignoreDiacritics: true, ignoreCase: true }));

                                tradeNames = this.props.state.RvqOptions.tradeNames
                                    .filter(x => x.Item.DisplayText.contains(searchString, { ignoreCase: true, ignoreDiacritics: true }));
                            }

                            if (existingMedicationPreferences.any() && tradeNames?.any()) {
                                tradeNames = tradeNames.filter(x => !existingMedicationPreferences.any(pref => pref.Item.Data?.QuebecVaccinationRegistryId === x.Item.Id));
                            }

                            if (existingMedicationPreferences.any() && tradeNames?.any()) {
                                tradeNames[0].CssClass = "border-top-style";
                            }

                            return [
                                ...existingMedicationPreferences,
                                ...tradeNames
                            ];
                        }}
                        valid={helper.isImmunizationNameValid}
                        renderItem={(model, cssClass) =>
                            !model?.Data
                                ? <span className={"ellipsis-auto " + (cssClass || "")} style={{ width: "100%" }}>{model.DisplayText}</span>
                                : (
                                    <RvqAutoCompleteRow
                                        model={model}
                                        onRvqPreferencesUpdate={preferences => this.props.mutateState(
                                            s => s.rvqMedicationPreferences = preferences,
                                            () => this.immunizationNameAutocompleteInstance.executeSearch(state.ImmunizationName)
                                        )}
                                    />
                                )
                        }
                    />
                )}
            />
        );
    }

    private renderRow(labelKey: string, element: React.ReactNode, isRendered: boolean = true, sideElement: React.ReactNode = null, key: string = null): JSX.Element {
        if (!isRendered)
            return null;

        return (
            <tr key={key || labelKey}>
                <td className="c1" style={{ verticalAlign: "middle" }}>
                    <span>{labelKey ? cacheStore.GetString(labelKey) : ""}</span>
                </td>
                <td className="c2" colSpan={2}>
                    {element}
                </td>
                <td>{sideElement ? sideElement : " "}</td>
            </tr>
        );
    }

    private renderAntigenSelector(): JSX.Element {
        const selectedItem = this.props.state
            .SelectedAntigens?.map(x => new CodeValue({ Code: x.Code, Label: QuebecVaccinationRegistryHelper.translateValueSetString(x, ValueSetType.Antigen) })) ?? [];

        return this.renderRow(
            "QuebecVaccinationRegistry_AntigenLabel",
            <div>
                <CodeValueSearchAutocompleteCustom
                    disabled={false}
                    componentId="add_antigens_vaccinationRefusal_Immunization"
                    selectedItemList={selectedItem.toImmutable()}
                    searchStrategy={this.props.state.codeValueSearchStrategy}
                    focusOnLoad={false}
                    closeDropDownAfterSelection
                    allowMultipleItemSelection
                    onClearCallback={(value: ValueCodeDropdownItem) => this.removeAntigen(value)}
                    onSelectionCallback={(value: ValueCodeDropdownItem) => this.onSelectAntigen(value)}
                    valid={this.props.state.IsRefused && (this.props.state.SelectedAntigens?.any() || !this.props.state.shouldSyncWithRvq)}
                />
            </div>
        );
    }

    private getVaccineRefusalElements(): JSX.Element[] {
        const refusalDate = this.props.state.RefusalDate;
        return [
            this.renderRow(
                "RefusedDateDLabel",
                <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                    <TextboxControlWithDatePicker
                        id="resolutionDP"
                        textValue={refusalDate.dateText}
                        dateValue={refusalDate.date}
                        onChange={(date: DateTimeWithPrecision) => this.props.mutateState(s => s.RefusalDate.onDateChange(date))}
                    />

                    {!this.isRvqEnabled && (
                        <div style={{ paddingLeft: "10px", alignSelf: "center" }} onClick={e => this.onRefusedDateApproxChange(e.target.checked)}>
                            <div style={{ float: "left", marginRight: "5px" }}>
                                <input
                                    disabled={this.lifeStagePrecisions.contains(refusalDate.datePrecision)}
                                    type="checkbox"
                                    onChange={e => this.onRefusedDateApproxChange(e.target.checked)}
                                    checked={refusalDate.isDateApproximative}
                                />
                            </div>
                            <span>Approx.</span>
                        </div>
                    )}

                    <div style={{ paddingLeft: "10px", cursor: "pointer", alignSelf: "center" }}>
                        <a
                            style={{ cursor: "pointer" }}
                            onClick={() => this.props.mutateState(s => s.RefusalDate = MedicalItemDate.today())}
                        >
                            <span>{cacheStore.GetString("TodayLabel")}</span>
                        </a>
                    </div>
                </div>
            ),
            this.renderRow(
                "QuebecVaccinationRegistry_MotiveLabel",
                <SelectKVPControl
                    Value={this.props.state.QuebecRegistryRefusalMotiveCode}
                    IncludeEmptyChoice
                    isInvalid={this.props.state.shouldSyncWithRvq && !this.props.state.QuebecRegistryRefusalMotiveCode}
                    AvailableValues={this.refusalMotiveReasons}
                    Callback={reasonCode => this.props.mutateState(s => s.QuebecRegistryRefusalMotiveCode = reasonCode)}
                />,
                this.isRvqEnabled
            )
        ];
    }

    private get administrationReasonOptions(): ComboBoxDropDownItem[] {
        return ComboBoxDropDownItem.fromDictionary(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.props.state.RvqOptions.administrationReasons, ValueSetType.AdministrationReason));
    }

    private get refusalMotiveReasons(): KeyValuePair<string, string>[] {
        return KeyValuePair.fromMapOfStrings(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.props.state.RvqOptions.vaccinationRefusalMotives, ValueSetType.VaccinationRefusalMotive));
    }

    private get isRvqEnabled(): boolean {
        return cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry;
    }

    private getVaccineDetailElements(): JSX.Element[] {
        const stateManager = this.stateManager;

        const state = this.props.state;
        const administrationDate = this.props.state.AdministrationDate;
        let adminDateDisabled = this.lifeStagePrecisions.contains(administrationDate.datePrecision) ? "disabled" : "";

        let practitionerList = QuebecVaccinationRegistryHelper.getSortedPreferenceList(state.rvqPractitionerPreferences);

        return [
            this.renderRow(
                "QuebecVaccinationRegistry_AdministrationReasonLabel",
                <SelectLookupControl
                    Value={state.QuebecRegistryAdministeredReasonCode}
                    IncludeEmptyChoice
                    invalid={!stateManager.isAdministrationReasonValid}
                    AvailableValues={this.administrationReasonOptions}
                    Callback={(reasonCode: string) => this.props.mutateState(s => s.QuebecRegistryAdministeredReasonCode = reasonCode)}
                />,
                this.isRvqEnabled && stateManager.isAdministrationReasonRequired
            ),
            this.renderRow(
                "AdministeredDateDLabel",
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ justifyContent: "flex-start", alignItems: "center", display: "flex" }}>
                        <TextboxControlWithDatePicker
                            id="resolutionDP"
                            textValue={administrationDate.dateText}
                            dateValue={administrationDate.date}
                            onChange={date => this.onAdministeredDateChange(state.AdministrationDate.onDateChange(date, !this.isRvqEnabled))}
                            isInvalid={!(MedicalItemDate.isValid(administrationDate, !this.isRvqEnabled, !this.isRvqEnabled) || (this.isRvqEnabled && !state.shouldSyncWithRvq))}
                            expectedPrecisionMinimum={this.isRvqEnabled ? LifeStageDatePrecisionEnum.Day : null}
                        />

                        {!this.isRvqEnabled && (
                            <div
                                style={{ paddingLeft: "10px" }}
                                onClick={() => this.onDateAdministeredApproxChange(!administrationDate.isDateApproximative)}
                            >
                                <div style={{ float: "left", marginRight: "5px" }}>
                                    <input
                                        disabled={adminDateDisabled === "disabled"}
                                        type="checkbox"
                                        onChange={e => this.onDateAdministeredApproxChange(e.target.checked)}
                                        checked={administrationDate.isDateApproximative}
                                    />
                                </div>
                                <span>Approx.</span>
                            </div>
                        )}
                        <div style={{ paddingLeft: "10px", cursor: "pointer" }}>
                            <a
                                style={{ cursor: "pointer" }}
                                onClick={() => this.onAdministeredDateChange(MedicalItemDate.today(!this.isRvqEnabled || state.AdministrationDate.isDateApproximative))}
                            >
                                <span>{cacheStore.GetString("TodayLabel")}</span>
                            </a>
                        </div>
                    </div>
                    {this.isRvqEnabled && !state.IsRefused && (
                        <CheckboxControl
                            id="isSelfReportCheckbox"
                            checked={this.props.state.IsQuebecRegistryHistoricalVaccine}
                            onChange={checked => this.onSelfReportChange(checked)}
                            label={cacheStore.GetString("QuebecVaccinationRegistry_HistoricalVaccineLabel")}
                            title={cacheStore.GetString("QuebecVaccinationRegistry_HistoricalVaccineToolTipMessage")}
                            disabled={!!this.props.state.QuebecRegistryId}
                        />
                    )}
                </div>
            ),
            this.renderRow(
                "QuebecVaccinationRegistry_ImmunizationLocationLabel",
                <RvqLocationSelection
                    selectedItem={!IsNullOrWhiteSpace(state.LocationDisplayText) && state.QuebecRegistryLocationId && state.QuebecRegistryLocationId !== ConstantValues.LocationUnknownId
                        ? { Id: state.QuebecRegistryLocationId, DisplayText: state.LocationDisplayText }
                        : null
                    }
                    preferences={this.props.state.rvqLocationPreferences}
                    onChange={(id, displayText, newPreferences) => {
                        this.props.mutateState(s => {
                            s.LocationDisplayText = displayText;
                            s.QuebecRegistryLocationId = id;
                            s.rvqLocationPreferences = newPreferences ?? s.rvqLocationPreferences;
                        });
                    }}
                    onPreferencesUpdate={(preferences, then) => this.props.mutateState(s => s.rvqLocationPreferences = preferences, then)}
                    isValid={stateManager.isLocationValid}
                    onClear={() => this.props.mutateState(s => {
                        s.QuebecRegistryLocationId = state.IsQuebecRegistryHistoricalVaccine ? ConstantValues.LocationUnknownId : null;
                        s.LocationDisplayText = "";
                    })}
                    isRvqAuthenticated={this.props.isRvqAuthenticated}
                />,
                this.isRvqEnabled
            ),

            this.renderRow(
                "VaccinatorLabel",
                this.isRvqEnabled
                    ? (
                        <SimpleSearchComplete<RvqPractitionerPreference>
                            ref={ref => this.administeredByAutocompleteInstance = ref}
                            componentId="AdministeredByAutocomplete"
                            selectedItemList={(
                                !IsNullOrWhiteSpace(state.AdministeredByDisplayText) && state.QuebecRegistryAdministeredById && state.QuebecRegistryAdministeredById !== ConstantValues.PractitionerUnknownId
                                    ? [
                                        {
                                            Id: state.QuebecRegistryAdministeredById,
                                            DisplayText: state.AdministeredByDisplayText
                                        }
                                    ]
                                    : []
                            ).toImmutable()}
                            onSelectionCallback={item => this.props.mutateState(s => {
                                s.AdministeredByDisplayText = item.Item.DisplayText;
                                s.QuebecRegistryAdministeredById = item.Item.Id as number;
                            })}
                            closeDropDownAfterSelection
                            doNotEraseListAfterSelection
                            onSearch={async searchString => {
                                const list = practitionerList.filter(x => x.Item.DisplayText.contains(searchString, { ignoreDiacritics: true, ignoreCase: true }));
                                list.forEach((x, index) => x.Active = index === 0);
                                return list;
                            }}
                            useBlurEvent={false}
                            interactiveOptions={[
                                {
                                    key: "addNewVaccinator",
                                    element: cacheStore.GetString("QuebecVaccinationRegistry_AddANewVaccinatorLabel"),
                                    onClick: () => this.mutateState(s => s.showPractitionerSearchPopup = true),
                                    style: { height: "30px", alignItems: "center", borderBottom: "1px solid #ccc", color: "#0c5baa", display: "flex" }
                                }
                            ]}
                            onClearCallback={() => this.props.mutateState(s => {
                                s.AdministeredByDisplayText = "";
                                s.QuebecRegistryAdministeredById = state.IsQuebecRegistryHistoricalVaccine ? ConstantValues.PractitionerUnknownId : null;
                            })}
                            valid={stateManager.isAdministeredByValid}
                            renderItem={model => (
                                <RvqAutoCompleteRow
                                    model={model}
                                    onRvqPreferencesUpdate={preferences => {
                                        this.props.mutateState(
                                            s => s.rvqPractitionerPreferences = preferences,
                                            () => this.administeredByAutocompleteInstance.executeSearch("")
                                        );
                                    }}
                                />
                            )}
                        />
                    )
                    : (
                        <PersonSearchAutocompleteCustom
                            componentId="immunization_vaccinator_edit"
                            selectedItemList={(
                                state.VaccinatorEmployeeId
                                    ? [Person.FromEmployee(cacheStore.GetEmployeeByContactFacetId(state.VaccinatorEmployeeId))]
                                    : []
                            ).toImmutable()}
                            onSelectionCallback={item => this.props.mutateState(s => s.VaccinatorEmployeeId = item.Item.ContactFacetId)}
                            onClearCallback={() => this.props.mutateState(s => s.VaccinatorEmployeeId = null)}
                            closeDropDownAfterSelection
                            searchStrategy={this.clinicianSearchStrategy}
                            onTextChange={text => this.props.mutateState(s => s.AdministeredByDisplayText = text)}
                            value={state.AdministeredByDisplayText}
                            disableClearOnSelect
                        />
                    )
            ),
            this.renderRow(
                "BoosterLabel",
                <span>
                    <TextboxControl
                        style={{ width: "100px", display: "inline-block", float: "left" }}
                        className="form-control"
                        IsNumeric
                        onChange={value => this.onBoosterTextChanged(value)}
                        value={state.Booster?.toString() ?? ""}
                    />
                    <SelectKVPControl
                        style={{ width: "200px", display: "inline-block", marginLeft: "5px", height: "35px" }}
                        IncludeEmptyChoice
                        Value={state.SelectedBoosterLookupId}
                        AvailableValues={[
                            new KeyValuePair("m", cacheStore.GetString("MonthsLabel")),
                            new KeyValuePair("y", cacheStore.GetString("YearsLabel"))
                        ]}
                        Callback={unitId => this.onBoosterUnitSelect(unitId)}
                    />
                </span>
            ),
            this.renderRow(
                "ManufacturerDLabel",
                <TextboxControl
                    onChange={value => this.props.mutateState(s => s.Manufacturer = value)}
                    value={state.Manufacturer}
                    className="form-control"
                />
            ),
            this.renderRow(
                "LotNumberDLabel",
                <div className="flex-container" style={{ justifyContent: "stretch", flexWrap: "wrap" }}>
                    <div style={{ flexGrow: 1, minWidth: "300px" }}>
                        {this.isRvqEnabled
                            ? (
                                <TextboxControl
                                    id="lotNumber"
                                    onChange={value => this.props.mutateState(s => {
                                        s.LotNumber = value;
                                        const lotNumber = stateManager?.lotNumberItems.firstOrDefault(x => x.LotNumber === value);
                                        s.QuebecRegistryLotId = lotNumber?.Id;
                                        s.Dose = null;
                                        s.QuebecRegistryDoseUnitCode = "";
                                        s.RouteCode = "";
                                    })}
                                    limit={null}
                                    listMaxHeight="241px"
                                    value={state.LotNumber}
                                    autoCompleteItems={stateManager.lotNumberItems
                                        .filter(lot => !lot.IsExpired || state.showExpiredLotNumbers)
                                        .map(lot => new SearchableItemModelT<MedicationBatchModel>(MedicationBatchModel.getLotDisplayName(lot), lot.Id.toString(), lot))
                                        .toImmutable()
                                    }
                                    onAutoCompleteSelectCallBack={stateManager.onLotNumberAutoComplete}
                                    valid={stateManager.isLotNumberValid}
                                />
                            )
                            : (
                                <TextboxControl
                                    id="lotNumber"
                                    onChange={value => this.props.mutateState(s => s.LotNumber = value)}
                                    value={state.LotNumber}
                                />
                            )
                        }
                    </div>
                    {this.isRvqEnabled && (
                        <div style={{ width: "250px", marginLeft: "10px", marginTop: "10px" }}>
                            <CheckboxControl
                                id="showExpiredLotNumber"
                                checked={state.showExpiredLotNumbers}
                                disabled={this.props.state.RvqOptions.batches.all(x => x.IsExpired)}
                                onChange={checked => this.props.mutateState(s => s.showExpiredLotNumbers = checked)}
                                label={cacheStore.GetString("QuebecVaccinationRegistry_ShowExpiredLotNumbers")}
                            />
                        </div>
                    )}
                </div>

            ),
            this.renderRow(
                "RouteDLabel",
                <SelectKVPControl
                    Value={state.RouteCode}
                    IncludeEmptyChoice
                    AvailableValues={stateManager.routeOptions}
                    Callback={routeId => this.props.mutateState(s => s.RouteCode = routeId)}
                    isInvalid={!stateManager.isRouteValid}
                />
            ),
            this.renderRow(
                "QuebecVaccinationRegistry_AdministrationSiteLabel",
                <SelectLookupControl {...stateManager.createSiteProps()} />
            ),
            this.renderRow(
                "QuebecVaccinationRegistry_QuantityAdministeredLabel",
                <span>
                    <TextboxControl
                        style={{ width: "100px", display: "inline-block", float: "left", height: "37px" }}
                        className="form-control"
                        IsNumeric
                        onChange={number => this.props.mutateState(s => s.Dose = number)}
                        value={state.Dose?.toString() ?? ""}
                        valid={stateManager.isDoseQuantityValid}
                    />
                    <SelectLookupControl
                        style={{
                            width: "200px",
                            display: "inline-block",
                            marginLeft: "5px",
                            height: "37px"
                        }}
                        IncludeEmptyChoice
                        invalid={!stateManager.isDoseUnitValid}
                        Value={stateManager.selectedDoseUnit}
                        AvailableValues={stateManager.doseUnitOptions}
                        Callback={(doseUnitCode: string) => stateManager.onDoseUnitChange(doseUnitCode)}
                    />
                </span>
            ),
            this.renderRow(
                "InstructionDLabel",
                <TextAreaResizable
                    style={{ resize: "none", minHeight: "100px" }}
                    verticalResizeOnly
                    id={"instructions" + (sessionStore.newId())}
                    text={state.Instruction}
                    isDisabled={false}
                    onChange={value => this.props.mutateState(s => s.Instruction = value)}
                    setCursorStartOnFocus={false}
                    resetTextOnIdChange
                />
            )
        ];
    }

    private onRefusedDateApproxChange(checked: boolean): void {
        this.props.mutateState(s => {
            s.RefusalDate = new MedicalItemDate({
                ...s.RefusalDate,
                isDateApproximative: checked,
                datePrecision: checked
                    ? LifeStageDatePrecisionEnum.DayApprox
                    : LifeStageDatePrecisionEnum.Day
            });
        });
    }

    private onDateAdministeredApproxChange(checked: boolean): void {
        this.onAdministeredDateChange(new MedicalItemDate({
            ...this.props.state.AdministrationDate,
            isDateApproximative: checked,
            datePrecision: checked
                ? LifeStageDatePrecisionEnum.DayApprox
                : LifeStageDatePrecisionEnum.Day
        }));
    }

    private onAdministeredDateChange(date: MedicalItemDate): void {
        const consultationDate = moment(this.props.encounter.ConsultationDate).startOf("day");

        this.props.mutateState(s => {
            s.AdministrationDate = date;
            if (this.isRvqEnabled && date.date) {
                s.IsQuebecRegistryHistoricalVaccine = moment(date.date).isBefore(consultationDate);
                this.stateManager.onHistoricalValueStateChange(s.IsQuebecRegistryHistoricalVaccine, s);
            }
        });
    }

    private onBoosterTextChanged(boosterTimeInterval: number): void {
        this.props.mutateState(s => {
            s.Booster = boosterTimeInterval;
            s.BoosterInMonths = s.SelectedBoosterLookupId == "y"
                ? boosterTimeInterval * 12
                : boosterTimeInterval;
        });
    }

    private onBoosterUnitSelect(unitId: string): void {
        this.props.mutateState(s => {
            s.SelectedBoosterLookupId = unitId;
            s.BoosterInMonths = s.Booster != null && s.SelectedBoosterLookupId == "y"
                ? s.Booster * 12
                : s.Booster;
        });
    }

    private removeAntigen(item: ValueCodeDropdownItem): void {
        if (!item || !item.Item)
            return;

        this.props.mutateState(s => s.SelectedAntigens = s.SelectedAntigens?.filter(x => x.Code !== item.Item.Code));
    }

    private onSelectAntigen(item: ValueCodeDropdownItem): void {
        if (!item || !item.Item)
            return;

        this.props.mutateState(s => {
            if (item.Item.Code == "SelectAll") {
                s.SelectedAntigens = s.AntigensInCurrentAgent;
            }
            else {
                if (!s.SelectedAntigens.contains(item.Item))
                    s.SelectedAntigens = [...s.SelectedAntigens, item.Item];
            }
        });
    }

    private onSelfReportChange(checked: boolean): void {
        this.props.mutateState(s => {
            s.IsQuebecRegistryHistoricalVaccine = checked;
            this.stateManager.onHistoricalValueStateChange(checked, s);
        });
    }

    private toggleFormType(formToToggle: ImmunizationDetailEditForm, toggleOn: boolean): void {
        const nextForm = toggleOn ? formToToggle : ImmunizationDetailEditForm.CreateImmunization;

        this.props.mutateState(
            s => {
                s.currentForm = nextForm;
                s.IsRefused = nextForm != ImmunizationDetailEditForm.CreateImmunization;
                if (this.isRvqEnabled) {
                    s.QuebecRegistryRefusalMotiveCode = null;
                    s.RefusalDate = MedicalItemDate.today();
                    if (s.IsRefused) {
                        MedicalItemsImmunizationViewState.clearNonRefusalFields(s);
                        s.QuebecRegistryRefusalMotiveCode = this.getQuebecRegistryRefusalMotiveCode();
                    }
                }
            },
            async () => {
                const state = this.props.state;
                if (state.IsRefused && nextForm === ImmunizationDetailEditForm.RefuseSpecificVaccine && (!!state.QuebecRegistryAgentCode || !!state.ImmunizingAgent))
                    this.stateManager.setAntigensByAgentNameOrCode(this.props.state.ImmunizingAgent, this.props.state.QuebecRegistryAgentCode);
            }
        );
    }

    private getQuebecRegistryRefusalMotiveCode(): string {
        return this.props.patientDateOfBirth
            ? PatientHelper.getPatientIsOfAgeOfConsent(this.props.patientDateOfBirth)
                ? ConstantValues.PatientRefusalReasonCode
                : ConstantValues.LegalRepresentativeRefusal
            : null;
    }

    private renderRefusalCheckboxes(): JSX.Element {
        const stateManager = this.stateManager;
        return (
            <div>
                <CheckboxControl
                    id="specificVaccineCheckbox"
                    checked={stateManager.isRefuseSpecificVaccineForm}
                    onChange={checked => this.toggleFormType(ImmunizationDetailEditForm.RefuseSpecificVaccine, checked)}
                    label={cacheStore.GetString("QuebecVaccinationRegistry_Refusal_SpecificVaccine")}
                    labelStyle={{ marginRight: "40px" }}
                    disabled={!!this.props.state.QuebecRegistryId || !!this.props.state.SubmittedToQuebecRegistryOn}
                />
                <CheckboxControl
                    id="allVaccinesCheckbox"
                    checked={stateManager.isRefuseAllVaccinesForm}
                    onChange={checked => this.toggleFormType(ImmunizationDetailEditForm.RefuseAllVaccines, checked)}
                    label={cacheStore.GetString("QuebecVaccinationRegistry_Refusal_AllVaccines")}
                    disabled={!!this.props.state.QuebecRegistryId || !!this.props.state.SubmittedToQuebecRegistryOn}
                />
            </div>
        );
    }

    private onImmunizationAgentChange(value: string, code?: string): void {
        this.props.mutateState(
            s => {
                s.ImmunizingAgent = value;
                if (this.isRvqEnabled) {
                    if (s.ImmunizationName && s.RvqOptions.tradeNames?.any(x => x.Item.DisplayText === s.ImmunizationName))
                        MedicalItemsImmunizationViewState.clearLinkedTradeNameFields(s);
                    s.SelectedAntigens = [];
                    s.QuebecRegistryAgentCode = code ?? "";
                    s.codeValueSearchStrategy = new CodeValueSearchStrategy([]);
                }
            },
            () => {
                if (this.isRvqEnabled)
                    this.stateManager.setAntigensByAgentNameOrCode(value, code);
            }
        );
    }
}

export { ImmunizationDetailsEditComponent, ImmunizationDetailsEditComponentProps };
